import React, { Fragment, useEffect } from 'react';
import useSession from '../Util/useSession';
import { useSnackbar } from 'notistack';
import Progress from '../Util/Progress';
import styled from 'styled-components';
import usePortalRunMetadata from './usePortalRunMetadata';
import useRunPipelineMetadata from './useRunPipelineMetadata';
import { RunStatusPipelineV2, RunStatusProgressV2 } from './RunStatusInfoV2';
import { hasRunMetadata, isOrchestratorPipeline } from './pipelineVersion';
import RunStatusFlow from './RunStatusFlow';
import RunSummaryOrchestrator from './RunSummaryOrchestrator';
import useSessionJobs, { toPercent } from '../Sessions/useSessionJobs';
import jobsService from '../../services/jobs';

interface Props {
  runid: string;
  sessionid: string | null | undefined;
}

interface BoxParams {
  border?: boolean;
}

const Box = styled.div<BoxParams>`
  width: 100%;
  height: 100%;
  border-right: ${(props) => (props.border ? '1px solid #e4e4e4' : 'none')};
`;

const RunStatus: React.FunctionComponent<Props> = (props) => {
  const { data: portalRun, loading: portalRunLoading } = usePortalRunMetadata(props.runid);
  const isMetadataRun = hasRunMetadata(portalRun?.pipelineVersion);
  const isOrchestratorRun = isOrchestratorPipeline(portalRun?.pipelineVersion);
  const { session, error: sessionerror } = useSession(props.sessionid);
  const { enqueueSnackbar } = useSnackbar();
  const runJob = useSessionJobs(props.sessionid || '').find(
    (job) => job.type === 'pipeline-run' && job.runId === props.runid && job.progress != null
  );

  useEffect(() => {
    if (props.sessionid) {
      jobsService.subscribeToSessions([props.sessionid]);
    }
  }, [props.sessionid]);

  if (sessionerror) {
    enqueueSnackbar('Failed to load session or run', { variant: 'error', autoHideDuration: 3000 });
  }
  const run = portalRun?.id ? { id: portalRun.id } : null;
  const { data: runMetadata, error: runMetadataError } = useRunPipelineMetadata(run?.id || undefined, isMetadataRun);

  return (
    <Fragment>
      <div className="sciui-card sciui-m-l sciui-bg-p-5">
        {portalRunLoading ? (
          <Progress />
        ) : (
          <div className="sciui-grid sciui-m-r-l" style={{ height: '100%' }}>
            {isOrchestratorRun && (
              <div className="sciui-grid-item-xs-12">
                {run ? <RunSummaryOrchestrator run={run} session={session} runMetadata={runMetadata} /> : <Progress />}
              </div>
            )}
            {!isOrchestratorRun && (
            <div className="sciui-grid-item-xs-12 sciui-grid-item-l-6" style={{ height: '100%' }}>
              <Box border>
                {isMetadataRun ? (
                  <RunStatusPipelineV2 portalRun={portalRun} runMetadata={runMetadata} error={runMetadataError} />
                ) : (
                  <span>No details available for this run.</span>
                )}
              </Box>
            </div>
            )}
            {isOrchestratorRun ? (
              <div className="sciui-grid-item-xs-12">
                {runMetadata ? <RunStatusFlow runMetadata={runMetadata} percent={runJob ? toPercent(runJob) : undefined} /> : <Progress />}
              </div>
            ) : (
              <div className="sciui-grid-item-xs-12 sciui-grid-item-l-6" style={{ height: '100%' }}>
                <Box border>
                  {run ? (
                    isMetadataRun && (
                      <RunStatusProgressV2 portalRun={portalRun} runMetadata={runMetadata} error={runMetadataError} />
                    )
                  ) : (
                    <Progress />
                  )}
                </Box>
              </div>
            )}
          </div>
        )}
      </div>
    </Fragment>
  );
};

export default RunStatus;
